import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { ADJUSTMENT_GROUPS, formatMoney } from './taxModel';

const inputClass = 'border-[#242E42] bg-[#0B0E14] text-[#F3F4F6]';
const NUMBER = /^-?\d+(\.\d{1,2})?$/;

export function AdjustmentsDialog({ open, onOpenChange, report, onSave, saving }) {
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !report) return;
    const initial = {};
    Object.entries(report.adjustments || {}).forEach(([key, entry]) => {
      Object.entries(entry).forEach(([field, value]) => { initial[`${key}|${field}`] = value; });
    });
    setValues(initial);
    setNotes(report.notes || '');
    setError('');
  }, [open, report]);

  const submit = (event) => {
    event.preventDefault();
    const adjustments = {};
    for (const [id, raw] of Object.entries(values)) {
      const text = String(raw).trim();
      if (!text) continue;
      if (!NUMBER.test(text)) { setError(`"${text}" no es un monto válido (usa números con hasta 2 decimales).`); return; }
      const [key, field] = id.split('|');
      adjustments[key] = { ...(adjustments[key] || {}), [field]: text };
    }
    setError('');
    onSave({ adjustments, notes });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-[#242E42] bg-[#151B26] text-[#F3F4F6]">
        <DialogHeader>
          <DialogTitle>Ajustes manuales — {report?.label}</DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">
            Lo que escribas se <strong>suma</strong> al valor calculado por el ERP (usa un número negativo para restar). Sirve para ventas o compras que no
            pasaron por el ERP, o para reclasificar. Cada casilla ajustada queda marcada en el borrador.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          {ADJUSTMENT_GROUPS.map((group) => (
            <fieldset key={group.title} className="space-y-3">
              <legend className="text-sm font-semibold text-[#E2B05E]">{group.title}</legend>
              {group.lines.map((line) => (
                <div key={line.label} className="grid items-center gap-2 sm:grid-cols-[1fr_auto]">
                  <Label className="text-xs text-[#D1D5DB]">{line.label}</Label>
                  <div className="flex gap-2">
                    {line.fields.map(([key, field, caption]) => (
                      <label key={`${key}|${field}`} className="block">
                        <span className="sr-only">{`${line.label} — ${caption}`}</span>
                        <Input
                          inputMode="decimal"
                          placeholder={caption}
                          value={values[`${key}|${field}`] || ''}
                          onChange={(event) => setValues((prev) => ({ ...prev, [`${key}|${field}`]: event.target.value }))}
                          className={`h-9 w-28 text-right text-sm ${inputClass}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </fieldset>
          ))}
          <div>
            <Label htmlFor="tax-notes" className="text-sm">Notas del período (salen en el borrador)</Label>
            <Textarea id="tax-notes" rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} className={inputClass} placeholder="Ej: ventas del 12 de mayo facturadas fuera del ERP" />
          </div>
          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#242E42]">Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">{saving ? 'Guardando…' : 'Guardar ajustes'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PresentDialog({ open, onOpenChange, report, onConfirm, saving }) {
  const [number, setNumber] = useState('');
  const [filed, setFiled] = useState('');
  const [paid, setPaid] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !report) return;
    setNumber('');
    setFiled(new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10));
    setPaid(report.form['59'] || '0.00');
    setError('');
  }, [open, report]);

  const submit = (event) => {
    event.preventDefault();
    if (number.trim().length < 3) { setError('Escribe el número de la declaración que da e-Tax (ej. 430010698500).'); return; }
    if (!filed) { setError('Indica la fecha de presentación.'); return; }
    if (!NUMBER.test(String(paid).trim()) || Number(paid) < 0) { setError('El monto pagado debe ser un número positivo.'); return; }
    setError('');
    onConfirm({ declaration_number: number.trim(), filed_date: filed, amount_paid: Number(paid) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-[#242E42] bg-[#151B26] text-[#F3F4F6]">
        <DialogHeader>
          <DialogTitle>Registrar presentación — {report?.label}</DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">
            Úsalo cuando tu contadora ya presentó la declaración en e-Tax. Se congelan las cifras de hoy, el período queda cerrado y el saldo a favor
            (casilla 54: {formatMoney(report?.form?.['54'])}) pasa al siguiente. Solo un administrador puede reabrirlo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="decl-number">Número de declaración</Label><Input id="decl-number" value={number} onChange={(e) => setNumber(e.target.value)} className={inputClass} autoFocus /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label htmlFor="decl-date">Fecha de presentación</Label><Input id="decl-date" type="date" value={filed} onChange={(e) => setFiled(e.target.value)} className={inputClass} /></div>
            <div><Label htmlFor="decl-paid">Monto pagado (B/.)</Label><Input id="decl-paid" inputMode="decimal" value={paid} onChange={(e) => setPaid(e.target.value)} className={inputClass} /></div>
          </div>
          {report && Number(report.form['59']) > 0 && <p className="text-xs text-[#9CA3AF]">Total a pagar calculado (casilla 59): B/. {formatMoney(report.form['59'])}. Si la contadora presentó con otras cifras, cierra este diálogo y regístralas como ajustes primero.</p>}
          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#242E42]">Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">{saving ? 'Guardando…' : 'Registrar y cerrar período'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SettingsDialog({ open, onOpenChange, settings, onSave, saving }) {
  const [form, setForm] = useState(settings || {});
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setForm(settings || {}); setError(''); } }, [open, settings]);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    if (!form.legal_name?.trim() || !form.ruc?.trim()) { setError('La razón social y el RUC son obligatorios.'); return; }
    if (!/^\d{0,2}$/.test(form.dv || '')) { setError('El DV son hasta 2 dígitos.'); return; }
    if (!/^\d{6}$/.test(form.first_period || '')) { setError('El primer período debe tener el formato AAAAMM, por ejemplo 202603.'); return; }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-[#242E42] bg-[#151B26] text-[#F3F4F6]">
        <DialogHeader>
          <DialogTitle>Datos del contribuyente</DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">Salen en el encabezado y en el bloque de firma del borrador.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="tax-frequency">Periodicidad de la declaración</Label>
            <select id="tax-frequency" value={form.frequency || 'quarterly'} onChange={(e) => update('frequency', e.target.value)} className={`h-10 w-full rounded-md border px-3 text-sm ${inputClass}`}>
              <option value="quarterly">Trimestral (como las declaraciones presentadas)</option>
              <option value="monthly">Mensual</option>
            </select>
            <p className="mt-1 text-xs text-amber-300">Confirma con tu contadora cuál corresponde antes de cambiarla.</p>
          </div>
          <div><Label htmlFor="tax-legal-name">Razón social (como figura en la DGI)</Label><Input id="tax-legal-name" value={form.legal_name || ''} onChange={(e) => update('legal_name', e.target.value)} className={inputClass} /></div>
          <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
            <div><Label htmlFor="tax-ruc">RUC</Label><Input id="tax-ruc" value={form.ruc || ''} onChange={(e) => update('ruc', e.target.value)} className={inputClass} /></div>
            <div><Label htmlFor="tax-dv">DV</Label><Input id="tax-dv" value={form.dv || ''} onChange={(e) => update('dv', e.target.value)} className={inputClass} /></div>
          </div>
          <div>
            <Label htmlFor="tax-first-period">Primer período a declarar (AAAAMM)</Label>
            <Input id="tax-first-period" value={form.first_period || ''} onChange={(e) => update('first_period', e.target.value)} className={inputClass} placeholder="202603" />
            <p className="mt-1 text-xs text-[#9CA3AF]">Los períodos anteriores no aparecen en la lista.</p>
          </div>
          <div><Label htmlFor="tax-rep-name">Representante legal — nombre</Label><Input id="tax-rep-name" value={form.representative_name || ''} onChange={(e) => update('representative_name', e.target.value)} className={inputClass} /></div>
          <div><Label htmlFor="tax-rep-id">Representante legal — cédula</Label><Input id="tax-rep-id" value={form.representative_id || ''} onChange={(e) => update('representative_id', e.target.value)} className={inputClass} /></div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={form.exclude_unidentified_buyer !== false} onChange={(e) => update('exclude_unidentified_buyer', e.target.checked)} />
            <span>Excluir del crédito fiscal las compras cuya factura no trae el nombre o RUC de la empresa
              <span className="block text-xs text-[#9CA3AF]">Recomendado. Se siguen mostrando como gastos de la empresa en el anexo. Desactívalo solo si tu contadora confirma que sí dan crédito.</span>
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(form.paragraph_9a)} onChange={(e) => update('paragraph_9a', e.target.checked)} /> Parágrafo 9-A (art. 1057-V del Código Fiscal)</label>
          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#242E42]">Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">{saving ? 'Guardando…' : 'Guardar'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
